import React from 'react'
import { Card } from '@components/ui/Card'
import { Icon } from '@components/ui/Icon'
import { getCardColor } from '@styles/design-tokens'
import { Square, RectangleHorizontal, Grid2x2, Grid3x3, Rows3 } from 'lucide-react'
import type { BrickType } from '@models/brick'
import './BrickSelector.css'

export interface BrickSelectorProps {
  selectedBrick: BrickType
  onSelectBrick: (type: BrickType) => void
}

/**
 * BrickSelector Component
 *
 * Card grid showing available brick types for placement
 *
 * Available brick types:
 * - 1x1: Single stud brick
 * - 1x2: 1 stud width, 2 studs length
 * - 2x2: 2x2 square brick
 * - 2x4: 2 studs width, 4 studs length
 * - 1x4: 1 stud width, 4 studs length
 *
 * Features:
 * - Colorful cards with rotation (pink/yellow/blue/purple/green)
 * - Large centered brick icon
 * - Selected state visualization
 * - Keyboard accessible
 * - Responsive grid layout
 *
 * @component
 */
export const BrickSelector: React.FC<BrickSelectorProps> = ({
  selectedBrick,
  onSelectBrick,
}) => {
  // All 5 brick types available (Task 4.2.1 complete)
  const brickTypes: Array<{ type: BrickType; label: string; icon: React.ReactNode }> = [
    { type: '1x1', label: '1x1', icon: <Square size={48} /> },
    { type: '1x2', label: '1x2', icon: <RectangleHorizontal size={48} /> },
    { type: '2x2', label: '2x2', icon: <Grid2x2 size={48} /> },
    { type: '2x4', label: '2x4', icon: <Grid3x3 size={48} /> },
    { type: '1x4', label: '1x4', icon: <Rows3 size={48} /> },
  ]

  return (
    <div className="brick-selector">
      <div className="brick-selector__label">BRICK TYPE</div>
      <div className="brick-selector__grid">
        {brickTypes.map((brick, index) => (
          <Card
            key={brick.type}
            backgroundColor={getCardColor(index)}
            selected={selectedBrick === brick.type}
            onClick={() => onSelectBrick(brick.type)}
            ariaLabel={`Select ${brick.label} brick`}
            className="brick-selector__card"
          >
            <Icon size="large" ariaLabel={brick.label}>
              {brick.icon}
            </Icon>
            <div className="brick-selector__card-label">{brick.label}</div>
          </Card>
        ))}
      </div>
    </div>
  )
}
